import { useState, useEffect, useRef } from 'react';
import { MapPin, Search, AlertTriangle, Building2, Globe, UserCheck, Home } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../utils';

const SiteSection = ({ formData, setFormData, siteSearch: siteState }) => {
  const {
    siteSearch, siteSuggestions, showSiteDropdown, setShowSiteDropdown,
    isSearchingSite, siteInputRef,
    nearbyInstallers, homeProximityAlternatives, checkingNearby,
    handleSiteSearchChange, selectSite,
  } = siteState;

  const [siteNameQuery, setSiteNameQuery] = useState(formData.site_name || '');
  const [pivaQuery, setPivaQuery] = useState('');
  const [siteNameSuggestions, setSiteNameSuggestions] = useState([]);
  const [impreseSuggestions, setImpreseSuggestions] = useState([]);
  const [showSiteNameDropdown, setShowSiteNameDropdown] = useState(false);
  const [isSearchingSiteName, setIsSearchingSiteName] = useState(false);
  const [isSearchingImprese, setIsSearchingImprese] = useState(false);
  const [impreseMessage, setImpreseMessage] = useState('');
  const siteNameRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    setSiteNameQuery(formData.site_name || '');
  }, [formData.site_name]);

  const searchSiteName = async (query) => {
    if (query.length < 2) {
      setSiteNameSuggestions([]);
      setShowSiteNameDropdown(false);
      return;
    }

    setIsSearchingSiteName(true);
    setShowSiteNameDropdown(true);

    try {
      const res = await axios.get(`${API}/appointments/sites/search?q=${encodeURIComponent(query)}`).catch(() => ({ data: [] }));
      setSiteNameSuggestions(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setIsSearchingSiteName(false);
    }
  };

  const searchByPiva = async (piva) => {
    const clean = piva.replace(/[^a-zA-Z0-9]/g, '');
    if (clean.length < 11) {
      setImpreseSuggestions([]);
      setImpreseMessage('');
      return;
    }

    setIsSearchingImprese(true);
    setImpreseMessage('');

    try {
      const res = await axios.get(`${API}/appointments/imprese/search?q=${encodeURIComponent(clean)}`);
      const data = res.data;
      setImpreseSuggestions(data.results || []);
      setImpreseMessage(data.message === 'OK' ? '' : data.message || '');
    } catch (e) {
      setImpreseMessage('Errore nella ricerca');
    } finally {
      setIsSearchingImprese(false);
    }
  };

  const handleSiteNameChange = (value) => {
    setSiteNameQuery(value);
    setFormData({ ...formData, site_name: value });

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchSiteName(value), 350);
  };

  const selectSiteFromDB = (site, location) => {
    const update = { site_name: site.site_name };
    if (location?.address) update.site_address = location.address;
    if (location?.city) { update.site_city = location.city; update.city = location.city; }
    if (location?.province) { update.site_province = location.province; update.province = location.province; }
    setFormData(prev => ({ ...prev, ...update }));
    setSiteNameQuery(site.site_name);
    setShowSiteNameDropdown(false);
    if (location?.address) handleSiteSearchChange(location.address);
  };

  const selectSiteFromImprese = (item) => {
    const update = { site_name: item.client_name };
    if (item.address) update.site_address = item.address;
    if (item.city) { update.site_city = item.city; update.city = item.city; }
    if (item.province) { update.site_province = item.province; update.province = item.province; }
    setFormData(prev => ({ ...prev, ...update }));
    setSiteNameQuery(item.client_name);
    setShowSiteNameDropdown(false);
    if (item.address) handleSiteSearchChange(item.address);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (siteNameRef.current && !siteNameRef.current.contains(e.target)) {
        setShowSiteNameDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="border border-blue-200 rounded-lg p-4 bg-blue-50/50">
      <div className="flex items-center gap-2 mb-3">
        <MapPin size={18} className="text-blue-600" />
        <label className="text-sm font-semibold text-blue-800">SITE - Luogo Esecuzione Lavoro</label>
      </div>

      {/* Ragione Sociale con Autocomplete */}
      <div className="mb-4 relative" ref={siteNameRef}>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Ragione Sociale SITE
          <span className="text-xs text-gray-400 ml-2">(digita per cercare)</span>
        </label>
        <div className="relative">
          <input
            type="text"
            value={siteNameQuery}
            onChange={(e) => handleSiteNameChange(e.target.value)}
            onFocus={() => siteNameQuery?.length >= 2 && setShowSiteNameDropdown(true)}
            className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="Es: FORD Vumbaca Group Transit Center"
            autoComplete="off"
            data-testid="appointment-site-name"
          />
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            {isSearchingSiteName ? (
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent"></div>
            ) : (
              <Search size={18} className="text-gray-400" />
            )}
          </div>
        </div>

        {showSiteNameDropdown && siteNameSuggestions.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-blue-200 rounded-lg shadow-lg max-h-60 overflow-y-auto" data-testid="site-name-dropdown">
            <div className="px-3 py-1.5 bg-gray-50 border-b text-xs font-semibold text-gray-500 flex items-center gap-1">
              <Building2 size={12} /> Gia utilizzate ({siteNameSuggestions.length})
            </div>
            {siteNameSuggestions.map((site, idx) => (
              <div key={`db-${idx}`}>
                {site.locations?.length > 0 ? (
                  site.locations.map((loc, locIdx) => (
                    <button key={`db-${idx}-${locIdx}`} type="button"
                      onClick={() => selectSiteFromDB(site, loc)}
                      className="w-full px-4 py-2.5 text-left hover:bg-blue-50 border-b last:border-b-0 transition">
                      <div className="font-medium text-gray-800">{site.site_name}</div>
                      {loc.address && <div className="text-xs text-gray-500">{loc.address}, {loc.city} {loc.province && `(${loc.province})`}</div>}
                      <div className="text-xs text-blue-500">{site.count} appuntamenti</div>
                    </button>
                  ))
                ) : (
                  <button type="button"
                    onClick={() => selectSiteFromDB(site, null)}
                    className="w-full px-4 py-2.5 text-left hover:bg-blue-50 border-b last:border-b-0 transition">
                    <div className="font-medium text-gray-800">{site.site_name}</div>
                    <div className="text-xs text-blue-500">{site.count} appuntamenti</div>
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Address Autocomplete */}
      <div className="relative">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Indirizzo SITE *
          <span className="text-xs text-gray-400 ml-2">(digita per cercare)</span>
        </label>
        <div className="relative">
          <input
            ref={siteInputRef}
            type="text"
            value={siteSearch}
            onChange={(e) => {
              handleSiteSearchChange(e.target.value);
              // Indirizzo digitato a mano -> proteggi dal sync SF (deselezionabile sotto)
              setFormData(prev => prev.manual_address ? prev : { ...prev, manual_address: true });
            }}
            onFocus={() => siteSearch?.length >= 3 && setShowSiteDropdown(true)}
            className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="Es: Via Roma 10, Milano"
            required={formData.confirmation_status !== 'provisional'}
            autoComplete="off"
            data-testid="appointment-site-address"
          />
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            {isSearchingSite ? (
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent"></div>
            ) : (
              <Search size={18} className="text-gray-400" />
            )}
          </div>
        </div>

        {showSiteDropdown && siteSuggestions.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-blue-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {siteSuggestions.map((site, idx) => (
              <button key={idx} type="button" onClick={() => selectSite(site)}
                className="w-full px-4 py-3 text-left hover:bg-blue-50 border-b last:border-b-0 transition">
                <div className="flex items-center gap-2">
                  <MapPin size={14} className="text-blue-600 flex-shrink-0" />
                  <div>
                    <div className="font-medium text-gray-800">{site.address || site.display_name.split(",")[0]}</div>
                    <div className="text-xs text-gray-500">{site.city} {site.province && `(${site.province})`}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        <label className="flex items-start gap-2 mt-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={!!formData.manual_address}
            onChange={(e) => setFormData(prev => ({ ...prev, manual_address: e.target.checked }))}
            className="mt-0.5 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            data-testid="manual-address-checkbox"
          />
          <span className="text-xs text-gray-600 flex items-center gap-1">
            <Home size={13} className="text-blue-600" />
            Domicilio cliente / indirizzo manuale — il Sync Salesforce <b>non</b> sovrascriverà questo indirizzo
          </span>
        </label>
      </div>

      <div className="grid grid-cols-2 gap-4 mt-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Citta {formData.confirmation_status !== 'provisional' && '*'}</label>
          <input
            type="text"
            value={formData.site_city || formData.city}
            onChange={(e) => setFormData({ ...formData, site_city: e.target.value, city: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            required={formData.confirmation_status !== 'provisional'}
            placeholder="Es: Milano"
            data-testid="appointment-site-city"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Provincia {formData.confirmation_status !== 'provisional' && '*'}</label>
          <input
            type="text"
            value={formData.site_province || formData.province}
            onChange={(e) => setFormData({ ...formData, site_province: e.target.value, province: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            maxLength={2}
            required={formData.confirmation_status !== 'provisional'}
            placeholder="MI"
            data-testid="appointment-site-province"
          />
        </div>
      </div>

      {/* Referente SITE */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Nome Referente {formData.confirmation_status !== 'provisional' && <span className="text-red-500">*</span>}
          </label>
          <input
            type="text"
            value={formData.contact_name || ""}
            onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            required={formData.confirmation_status !== 'provisional'}
            placeholder="Es: Mario Rossi"
            data-testid="appointment-contact-name"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Telefono Referente {formData.confirmation_status !== 'provisional' && <span className="text-red-500">*</span>}
          </label>
          <input
            type="tel"
            value={formData.contact_phone || ""}
            onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            required={formData.confirmation_status !== 'provisional'}
            placeholder="Es: 333 1234567"
            data-testid="appointment-contact-phone"
          />
        </div>
      </div>

      {/* Nearby Installers Alert */}
      {checkingNearby && (
        <div className="mt-3 p-3 bg-gray-100 rounded-lg text-center">
          <div className="animate-spin inline-block rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent mr-2"></div>
          <span className="text-sm text-gray-600">Controllo installatori nella zona...</span>
        </div>
      )}

      {!checkingNearby && nearbyInstallers.length > 0 && (() => {
        const reds = nearbyInstallers.filter(i => i.alert_level === 'red');
        const yellows = nearbyInstallers.filter(i => i.alert_level === 'yellow' || !i.alert_level);
        return (
          <div className="mt-3 space-y-2" data-testid="nearby-installers-alarm">
            {reds.length > 0 && (
              <div className="p-3 bg-red-50 border border-red-300 rounded-lg" data-testid="alert-red">
                <div className="flex items-start gap-2">
                  <AlertTriangle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-red-800">
                      Altro tecnico nella stessa area ({reds.length}) - possibile sovrapposizione
                    </p>
                    <p className="text-xs text-red-600 mb-2">Entro 10 km. Clicca per riassegnare.</p>
                    <div className="space-y-1">
                      {reds.slice(0, 5).map((inst, idx) => (
                        <button key={`r-${idx}`} type="button"
                          onClick={() => {
                            setFormData(prev => ({
                              ...prev,
                              team_id: inst.team_id || prev.team_id,
                              assigned_installer_id: inst.installer_id,
                            }));
                            toast.success(`Installatore cambiato: ${inst.installer_name}`);
                          }}
                          className="w-full text-xs bg-white p-2 rounded border border-red-200 hover:border-red-400 hover:bg-red-50 transition text-left flex items-center gap-2 group"
                          data-testid={`alert-red-${idx}`}>
                          <div className="flex-1 min-w-0">
                            <span className="font-medium text-gray-800">{inst.installer_name}</span>
                            {inst.team_name && <span className="text-gray-400 ml-1">({inst.team_name})</span>}
                            <span className="text-gray-500"> - {inst.scheduled_time} - </span>
                            <span className="text-red-700 font-bold">{inst.distance_km} km</span>
                            <div className="text-gray-500 truncate">{inst.client_name} - {inst.address}</div>
                          </div>
                          <UserCheck size={14} className="text-red-400 opacity-0 group-hover:opacity-100 transition flex-shrink-0" />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {yellows.length > 0 && (
              <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg" data-testid="alert-yellow">
                <div className="flex items-start gap-2">
                  <AlertTriangle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-amber-800">
                      Altro tecnico nella stessa zona ({yellows.length}) - valuta accorpamento
                    </p>
                    <p className="text-xs text-amber-600 mb-2">Tra 10 e 60 km. Clicca per riassegnare.</p>
                    <div className="space-y-1">
                      {yellows.slice(0, 5).map((inst, idx) => (
                        <button key={`y-${idx}`} type="button"
                          onClick={() => {
                            setFormData(prev => ({
                              ...prev,
                              team_id: inst.team_id || prev.team_id,
                              assigned_installer_id: inst.installer_id,
                            }));
                            toast.success(`Installatore cambiato: ${inst.installer_name}`);
                          }}
                          className="w-full text-xs bg-white p-2 rounded border border-amber-200 hover:border-amber-400 hover:bg-amber-50 transition text-left flex items-center gap-2 group"
                          data-testid={`alert-yellow-${idx}`}>
                          <div className="flex-1 min-w-0">
                            <span className="font-medium text-gray-800">{inst.installer_name}</span>
                            {inst.team_name && <span className="text-gray-400 ml-1">({inst.team_name})</span>}
                            <span className="text-gray-500"> - {inst.scheduled_time} - </span>
                            <span className="text-amber-700 font-medium">{inst.distance_km} km</span>
                            <div className="text-gray-500 truncate">{inst.client_name} - {inst.address}</div>
                          </div>
                          <UserCheck size={14} className="text-amber-400 opacity-0 group-hover:opacity-100 transition flex-shrink-0" />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {!checkingNearby && homeProximityAlternatives && homeProximityAlternatives.length > 0 && (
        <div className="mt-3 p-3 bg-blue-50 border border-blue-300 rounded-lg" data-testid="alert-blue">
          <div className="flex items-start gap-2">
            <Home size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-blue-800">
                Tecnici con domicilio piu vicino al cliente ({homeProximityAlternatives.length})
              </p>
              <p className="text-xs text-blue-600 mb-2">
                Primo appuntamento del giorno: valuta riassegnazione (soglia 20 km piu vicino)
              </p>
              <div className="space-y-1">
                {homeProximityAlternatives.slice(0, 5).map((alt, idx) => (
                  <button key={`b-${idx}`} type="button"
                    onClick={() => {
                      setFormData(prev => ({
                        ...prev,
                        team_id: alt.team_id || prev.team_id,
                        assigned_installer_id: alt.installer_id,
                      }));
                      toast.success(`Installatore cambiato: ${alt.installer_name}`);
                    }}
                    className="w-full text-xs bg-white p-2 rounded border border-blue-200 hover:border-blue-400 hover:bg-blue-50 transition text-left flex items-center gap-2 group"
                    data-testid={`alert-blue-${idx}`}>
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-gray-800">{alt.installer_name}</span>
                      {alt.team_name && <span className="text-gray-400 ml-1">({alt.team_name})</span>}
                      <div className="text-gray-500">
                        Casa a <span className="text-blue-700 font-medium">{alt.home_distance_km} km</span>
                        {' '}dal cliente
                        {' '}(<span className="text-green-700">-{alt.delta_km} km</span> rispetto al selezionato)
                      </div>
                    </div>
                    <UserCheck size={14} className="text-blue-400 opacity-0 group-hover:opacity-100 transition flex-shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SiteSection;
